/*!
 * paramBinding.js — Parameter Binding & Signal Publishing
 * Part of oscillaScore control plane architecture
 * © 2025 Rob Canning — GPLv3
 *
 * This module provides:
 *   - bindParam(): Subscribe a parameter to a signal source
 *   - publish(): Publish signals from animations
 *   - isSignalRef(): Check if a value is a signal reference
 *
 * Usage:
 *   // In synth - bind freq to a fader
 *   const freqBinding = bindParam(params.freq, (hz) => osc.frequency.value = hz, { min: 20, max: 2000 });
 *
 *   // In animation - publish position
 *   publish("o2p", uid, { t: 0.5, x: 0.3, y: 0.7 });
 */

import * as ParamBus from './paramBus.js';
import { isCombine, bindingSources } from '../parser/parserSignalRef.js';
import { isModulator, modSpec, modWave } from '../cues/audio/modShapes.js';
import { evalMaybeRandom } from '../cues/audio/patterns.js';
import { gridNow } from '../cues/audio/beatGrid.js';
import { applyCurve } from './curves.js';
import { onReset } from "../system/teardown.js";

// ===========================
// Signal Reference Detection
// ===========================

/**
 * Is this something bindParam can bind?
 *
 * Every caller in the cue layer asks this to decide "does this parameter follow
 * a signal, or is it just a number", so it has to answer yes for arithmetic too
 * — a combine node reads several sources but is a binding like any other.
 * A gate on the strict meaning would silently treat mul(…) as a number and
 * bind nothing.
 *
 * parserSignalRef.js keeps a strict isSignalRef for code that genuinely means
 * "is this one signalRef".
 */
export function isBindable(value) {
  return !!value && typeof value === 'object'
    && (value.type === 'signalRef' || value.type === 'combine');
}

/**
 * @deprecated the original name. Kept because a dozen cue modules import it,
 * and because at every one of those call sites "bindable" is what was meant.
 */
export const isSignalRef = isBindable;

/**
 * Something that moves by itself or follows something: a follow() (as
 * isBindable), or sinOsc() and its family -- which a sound plays at audio
 * rate (synth.js) and which anything else (a shape's at:, a screen effect, a
 * viz width ...) gets as a value each frame (bindParam, modulatorBinding).
 */
export const isMoving = value => isBindable(value) || isModulator(value);

export function isOscRef(value) {
  return value != null && typeof value === 'object' && value.type === 'oscRef';
}


// ===========================
// Range Mapping
// ===========================

// applyCurve: curves.js, one table with its inverse and the parser's names
export { applyCurve };

/**
 * Map a value from one range to another with optional curve
 * @param {number} value - Input value
 * @param {number} inMin - Input range minimum
 * @param {number} inMax - Input range maximum
 * @param {number} outMin - Output range minimum
 * @param {number} outMax - Output range maximum
 * @param {string} curve - Curve type: 'lin', 'exp2', 'exp3', 'exp4', 'log', 'sqrt'
 * @returns {number} Mapped value
 */
export function mapRange(value, inMin, inMax, outMin, outMax, curve = 'lin') {
  if (inMax === inMin) return outMin;
  const normalized = (value - inMin) / (inMax - inMin);
  const curved = applyCurve(normalized, curve);
  return outMin + curved * (outMax - outMin);
}


// ===========================
// Parameter Binding
// ===========================

/**
 * Live bindings that own their own teardown, keyed by "<kind>:<uid>:<param>".
 *
 * Passing `owner` means binding again under the same key releases the previous
 * one first, so re-assigning a cue replaces its subscription rather than
 * stacking another on the same signal, and nobody has to remember to tidy up.
 *
 * Per-VOICE bindings (synth) deliberately do not use this — several voices can
 * be live under one uid at once, so they are owned by the voice
 * (synth.js's voice._unbinders).
 */
const ownedBindings = new Map();

// The live console's lines that made a binding -- owner -> the line as
// written -- so the console can mark them "active" and Ctrl+Enter can stop
// them; and where a stopped one goes back to (screen(invert:...) to 0).
const ownerSources = new Map();
const ownerRests = new Map();
const ownerSeq = new Map();          // owner -> when it was bound (bindSeq)
let bindSeq = 0;
const sourcesChanged = () => {
  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') window.dispatchEvent(new CustomEvent('oscilla:sound-state'));
};

/**
 * Run fn(); the owned bindings made meanwhile belong to `source` (a console
 * line). A cue's handler may be loaded first (import()), so they are claimed
 * once fn's promise settles too -- those bound since it began and not
 * claimed yet.
 */
export function bindingsFrom(source, fn) {
  const from = bindSeq;
  const claim = () => {
    let changed = false;
    for (const [owner, seq] of ownerSeq) {
      if (seq > from && !ownerSources.has(owner)) { ownerSources.set(owner, source); changed = true; }
    }
    if (changed) sourcesChanged();
  };
  const result = fn();
  claim();
  if (result && typeof result.then === 'function') result.then(claim, claim);
  return result;
}

/** The console lines following a signal now: [{ owner, source }]. */
export function liveBindings() {
  return [...ownerSources].map(([owner, source]) => ({ owner, source }));
}

/** Stop what this console line follows, each back to its rest. How many. */
export function stopBindingsOf(source) {
  let n = 0;
  for (const [owner, s] of [...ownerSources]) {
    if (s !== source) continue;
    const rest = ownerRests.get(owner);
    releaseBinding(owner);
    try { rest?.(); } catch (err) { console.warn('[bindParam] rest failed:', err); }
    n++;
  }
  return n;
}

/** Release a binding held under this owner key, if any. */
export function releaseBinding(owner) {
  const prev = ownedBindings.get(owner);
  if (!prev) return false;
  try { prev(); } catch (err) { console.warn('[bindParam] release failed:', err); }
  ownedBindings.delete(owner);
  ownerRests.delete(owner); ownerSeq.delete(owner);
  if (ownerSources.delete(owner)) sourcesChanged();
  return true;
}

/** Release every owned binding — a project change, or a full rescan. */
export function releaseAllBindings() {
  let n = 0;
  for (const owner of [...ownedBindings.keys()]) if (releaseBinding(owner)) n++;
  return n;
}
onReset("project", releaseAllBindings);

/** How many owned bindings are live. For tests and the live console. */
export function ownedBindingCount() {
  return ownedBindings.size;
}

/** The bus path a follow()/map() term reads: osc:<address> without its slash, as OSC in writes it. */
function termPath(ref) {
  return ref.type === 'oscRef' ? `osc:${ref.address.replace(/^\//, '')}` : `${ref.source}.${ref.channel}`;
}

/** A combine's paths for display, a nested combine in brackets. */
function combinePath(node) {
  return node.terms.filter((t) => typeof t !== 'number')
    .map((t) => isCombine(t) ? `(${combinePath(t)})` : termPath(t))
    .join(` ${node.op} `);
}

/**
 * A term's value right now, already mapped into its own output range.
 * Falls back to the fold's identity (1 for mul, 0 for add) so a source that
 * has not published yet leaves the product or sum unchanged rather than
 * collapsing it to zero.
 */
function readTermNow(ref, identity) {
  const raw = ParamBus.get(termPath(ref));
  if (!Number.isFinite(raw)) return identity;
  return mapRange(raw, ref.inRange?.[0] ?? 0, ref.inRange?.[1] ?? 1,
                  ref.range?.[0] ?? 0, ref.range?.[1] ?? 1, ref.curve || 'lin');
}

// ── sinOsc() and its family, for what is not a sound ─────────────────────────
// Each frame, the shape's value at the shared clock's time (gridNow): every
// machine in a room in the same phase, and a line run again not restarting
// it. phase: 0..1 of a cycle on from there. One frame loop for all of them.
const modBindings = new Set();
let modRaf = 0;
function modStep() {
  const t = gridNow().ms / 1000;
  for (const b of modBindings) b.tick(t);
}
function modFrame() {
  modRaf = 0;
  if (!modBindings.size) return;
  modStep();
  modRaf = requestAnimationFrame(modFrame);
}
// A hidden page has no frames: the worker clock (backgroundClock.js, in
// both apps) steps them instead, every 16 ms while hidden.
const modBackground = () => { if (modBindings.size) modStep(); };
if (typeof window !== 'undefined') {
  window.oscillaBackgroundClock?.onBackgroundTick(modBackground);
  window.addEventListener?.("oscilla:background-clock-ready", () => {
    window.oscillaBackgroundClock?.onBackgroundTick(modBackground);
  }, { once: true });
}

/** A modulator's value at time t (s): lo..hi. Pure but for its table, made once. */
export function modulatorAt(spec, table, t) {
  const [wave, perUnit] = table;
  const cycles = spec.rate * t + spec.phase;
  // the cyclic shapes: perUnit samples a cycle; the noises: perUnit samples a point, rate points a second
  const pos = ((cycles * perUnit) % wave.length + wave.length) % wave.length;
  const w = wave[Math.floor(pos)];
  return spec.lo + (spec.hi - spec.lo) * (w + 1) / 2;
}

function modulatorBinding(v, onUpdate, { min, max }) {
  const spec = modSpec(v, evalMaybeRandom);
  const table = modWave(spec);
  const clamp = x => Math.min(Number.isFinite(max) ? max : Infinity, Math.max(Number.isFinite(min) ? min : -Infinity, x));
  const b = { tick: t => { try { onUpdate(clamp(modulatorAt(spec, table, t))); } catch (err) { console.error('[bindParam] modulator update error:', err); } } };
  modBindings.add(b);
  if (!modRaf && typeof requestAnimationFrame === 'function') modRaf = requestAnimationFrame(modFrame);
  const value = clamp(modulatorAt(spec, table, gridNow().ms / 1000));
  return { value, unbind: () => modBindings.delete(b), isBinding: true, path: `mod:${spec.shape}` };
}

/**
 * Bind a parameter to either a static value or a signal source
 *
 * @param {any} paramValue - Static value, signalRef, oscRef, combine or modulator from the parser
 * @param {Function} onUpdate - Called when signal changes: (mappedValue, rawValue) => {}
 * @param {Object} options - Binding options
 * @param {number} options.min - Output range minimum (default: 0); a combine is clamped only when given
 * @param {number} options.max - Output range maximum (default: 1); likewise
 * @param {number} options.default - Default value if signal not yet available
 * @param {number} options.smoothing - Smoothing factor 0-0.99 (default: 0)
 * @param {string} options.curve - Curve type: 'lin', 'exp2', 'exp3', 'exp4', 'log', 'sqrt'
 * @param {string} options.owner - Key under which a later binding replaces this one
 * @returns {{ value: number, unbind: Function, isBinding: boolean }}
 */
export function bindParam(paramValue, onUpdate, options = {}) {
  const {
    min = 0,
    max = 1,
    default: defaultValue,
    smoothing = 0,
    curve: optionsCurve = 'lin',
    owner = null,
    rest = null
  } = options;

  // An owned binding replaces whatever was previously bound under the same key.
  if (owner) releaseBinding(owner);
  const own = (result) => {
    if (owner && result?.isBinding && typeof result.unbind === 'function') {
      ownedBindings.set(owner, result.unbind);
      if (rest) ownerRests.set(owner, rest);
      ownerSeq.set(owner, ++bindSeq);
      const release = result.unbind;
      result.unbind = () => {
        ownedBindings.delete(owner); ownerRests.delete(owner); ownerSeq.delete(owner);
        if (ownerSources.delete(owner)) sourcesChanged();
        release();
      };
    }
    return result;
  };

  // sinOsc() and its family, for anything but a sound's own parameters
  // (options.audio: synth.js plays those at audio rate, attachModulator)
  if (isModulator(paramValue) && !options.audio) {
    return own(modulatorBinding(paramValue, onUpdate, { min, max }));
  }

  // OSC reference. Raw passthrough unless follow(/addr, min, max) gave a range —
  // in which case the incoming 0-1 is mapped like any other binding. Without
  // this the range in the score would be written, read, and then ignored.
  if (isOscRef(paramValue)) {
    const oscKey = `osc:${paramValue.address.replace(/^\//, '')}`;
    const oscRange = paramValue.range;
    const oscCurve = paramValue.curve || optionsCurve;
    const oscDefault = paramValue.default ?? defaultValue;
    const oscIn = paramValue.inRange;
    const mapOsc = (raw) => (oscRange
      ? mapRange(raw, oscIn?.[0] ?? 0, oscIn?.[1] ?? 1, oscRange[0], oscRange[1], oscCurve)
      : raw);

    const currentRaw = ParamBus.get(oscKey);
    const initialValue = (currentRaw !== undefined && Number.isFinite(currentRaw))
      ? mapOsc(currentRaw)
      : (oscDefault ?? (oscRange ? oscRange[0] : 0));
    const unbind = ParamBus.subscribe(oscKey, (rawValue) => {
      if (Number.isFinite(rawValue)) {
        try { onUpdate(mapOsc(rawValue), rawValue); } catch (err) { console.error('[bindParam] OSC update error:', err); }
      }
    });
    return own({ value: initialValue, unbind, isBinding: true, path: oscKey });
  }

  // ── Arithmetic: mul(a, b) / add(a, b) ────────────────────────────────────
  //
  // One binding, several sources. Each term is a follow()/map() that already
  // knows its own range, so this layer only has to keep the latest value of
  // each and recompute when any of them moves.
  //
  // The recompute is why this could not be done by nesting bindParam calls: a
  // subscription fires with ONE source's value, and the result depends on all
  // of them. The cache is seeded from the bus so a term that has already
  // published is not treated as zero until it next moves.
  if (isCombine(paramValue)) {
    const terms = paramValue.terms;
    const unit = paramValue.op === 'mul' ? 1 : 0;      // identity for the fold
    const latest = terms.map((t) =>
      typeof t === 'number' || isCombine(t) ? t : readTermNow(t, unit));
    // Clamp to the TARGET's range when the caller declares one. Each term is
    // already inside its own range, but a product or a sum is not, and an
    // unclamped result can put a scale at 1e6 from one mistyped range.
    const lo = 'min' in options ? min : -Infinity;
    const hi = 'max' in options ? max : Infinity;

    const combine = () => {
      const raw = paramValue.op === 'mul'
        ? latest.reduce((acc, v) => acc * v, 1)
        : latest.reduce((acc, v) => acc + v, 0);
      return Math.min(Math.max(raw, lo), hi);
    };

    let current;
    let seeded = false;
    const unsubs = [];
    const moved = (i, v, rawValue) => {
      latest[i] = v;
      current = combine();
      try { onUpdate(current, rawValue); }
      catch (err) { console.error('[bindParam] combine handler error:', err); }
    };
    terms.forEach((t, i) => {
      if (typeof t === 'number') return;
      if (isCombine(t)) {
        // A nested mul()/add() is a binding of its own, unclamped; its value is this term.
        const sub = bindParam(t, (v) => { if (seeded) moved(i, v, v); });
        latest[i] = sub.value;
        unsubs.push(sub.unbind);
        return;
      }
      unsubs.push(ParamBus.subscribe(termPath(t), (rawValue) => {
        if (!Number.isFinite(rawValue)) return;
        moved(i, mapRange(rawValue, t.inRange?.[0] ?? 0, t.inRange?.[1] ?? 1,
                          t.range?.[0] ?? 0, t.range?.[1] ?? 1, t.curve || 'lin'), rawValue);
      }));
    });
    current = combine();
    seeded = true;

    if (bindingSources(paramValue).some((t) => ParamBus.get(termPath(t)) !== undefined)) {
      try { onUpdate(current, current); }
      catch (err) { console.error('[bindParam] combine initial error:', err); }
    }

    return own({
      value: current,
      unbind: () => unsubs.forEach((u) => u()),
      isBinding: true,
      path: combinePath(paramValue),
      curve: optionsCurve,
    });
  }

  // Static value - just return it
  if (!isSignalRef(paramValue)) {
    // Handle numbers, numeric strings, and note names
    let staticValue;
    if (typeof paramValue === 'number') {
      staticValue = paramValue;
    } else if (typeof paramValue === 'string') {
      const parsed = parseFloat(paramValue);
      staticValue = Number.isFinite(parsed) ? parsed : (defaultValue ?? min);
    } else {
      staticValue = defaultValue ?? min;
    }
    return {
      value: staticValue,
      unbind: () => {},
      isBinding: false
    };
  }

  // Signal reference - set up subscription
  const { source, channel, range, inRange, curve: signalCurve } = paramValue;

  // Determine output range (signal range overrides options)
  const outMin = range?.[0] ?? min;
  const outMax = range?.[1] ?? max;

  // Which part of the source to read. follow() assumes the whole 0-1 sweep;
  // map() names a window, so a slow or narrow source can still drive a
  // parameter across its full span. mapRange has always taken an input range —
  // every caller simply passed 0,1 — so this is that argument finally reaching
  // it from the score.
  const inMin = inRange?.[0] ?? 0;
  const inMax = inRange?.[1] ?? 1;

  // Curve from signal ref takes priority over options
  const curve = signalCurve || optionsCurve;

  // A rangeless follow() into a caller that declares no range reads the signal
  // as-is. Mapping 0-1 → 0-1 was a no-op for a fader's t but clamped vel,
  // tvel and angle to 1, so display(v: follow(obj.tvel)) sat at 1 all lap.
  const passthrough = !range && !inRange && !('min' in options) && !('max' in options) && curve === 'lin';
  const map = (v) => passthrough ? v : mapRange(v, inMin, inMax, outMin, outMax, curve);

  // Build source-agnostic signal path.
  // The composer writes "freq:fader1.t[200,2000]" — the parser extracts
  // source="fader1", channel="t".  We subscribe to "fader1.t" which is
  // the agnostic path that publish() writes regardless of the source type
  // (o2p, controlXY, rotate, scale, color, fade, oscCtrl, ...).
  const path = `${source}.${channel}`;

  // Smoothing state
  let smoothedValue = null;

  // Get initial value if signal already exists
  const currentRaw = ParamBus.get(path);
  let initialValue = defaultValue ?? outMin;
  let hasExistingValue = false;

  if (currentRaw !== undefined && Number.isFinite(currentRaw)) {
    initialValue = map(currentRaw);
    smoothedValue = initialValue;
    hasExistingValue = true;
  }

  // Subscribe to updates
  const unbind = ParamBus.subscribe(path, (rawValue, signalPath, meta) => {
    if (!Number.isFinite(rawValue)) return;

    // Map to output range with curve
    let mapped = map(rawValue);

    // Apply smoothing if enabled
    if (smoothing > 0 && smoothedValue !== null) {
      mapped = smoothedValue * smoothing + mapped * (1 - smoothing);
    }
    smoothedValue = mapped;

    // Call update handler
    try {
      onUpdate(mapped, rawValue);
    } catch (err) {
      console.error('[bindParam] Update handler error:', err);
    }
  });


  // If there was already a value when we subscribed, call the handler immediately
  // This handles late-join scenarios where fader published before scale subscribed
  if (hasExistingValue) {
    if (window.OSCILLA_BINDING_DEBUG) console.log(`[bindParam] Applying existing value for ${path}: ${initialValue}`);
    try {
      onUpdate(initialValue, currentRaw);
    } catch (err) {
      console.error('[bindParam] Initial update handler error:', err);
    }
  }

  return own({
    value: initialValue,
    unbind,
    isBinding: true,
    path,
    curve
  });
}


// ===========================
// Signal Publishing
// ===========================

/** @type {Map<string, number>} Rate limiting timestamps */
const publishTimestamps = new Map();

/** Values held back by the throttle, sent when their window ends. */
const pendingPublishes = new Map();

/** @type {number} Minimum interval between publishes (ms) */
const PUBLISH_THROTTLE_MS = 16; // ~60fps

/**
 * Publish signals from an animation or cue
 * 
 * @param {string} sourceType - Source type: "o2p", "rotate", "scale", "synth", "audio"
 * @param {string} uid - Unique identifier of the source
 * @param {Object} channels - Channel name -> value mapping
 * @param {Object} meta - Optional metadata
 * 
 * @example
 * // In o2p animation update:
 * publish("o2p", cfg.uid, { t: pathT, x: normX, y: normY, angle });
 * 
 * // In rotate animation update:
 * publish("rotate", cfg.uid, { angle: currentAngle });
 */
export function publish(sourceType, uid, channels, meta = {}) {
  const now = performance.now();
  const throttleKey = `${sourceType}:${uid}`;

  // Throttle publishing -- but keep the last word. A value inside the window
  // is held and sent when the window ends, or the final position of a fast
  // drag, or where a motion was stopped, never reaches the bus and whatever
  // follows it stays at a stale value.
  const lastTime = publishTimestamps.get(throttleKey) || 0;
  const held = pendingPublishes.get(throttleKey);
  if (now - lastTime < PUBLISH_THROTTLE_MS) {
    if (held) { held.channels = channels; held.meta = meta; return; }
    const entry = { channels, meta };
    entry.timer = setTimeout(() => {
      pendingPublishes.delete(throttleKey);
      publish(sourceType, uid, entry.channels, entry.meta);
    }, PUBLISH_THROTTLE_MS - (now - lastTime));
    pendingPublishes.set(throttleKey, entry);
    return;
  }
  // a newer value is going out now: the held one would only overwrite it
  if (held) { clearTimeout(held.timer); pendingPublishes.delete(throttleKey); }
  publishTimestamps.set(throttleKey, now);

  // ONE path per channel: "<uid>.<channel>" — the form bindParam subscribes
  // to. Source type is in the meta below for anyone who needs it.
  for (const [channel, value] of Object.entries(channels)) {
    if (value === undefined || value === null) continue;
    if (!Number.isFinite(value)) continue;

    const pubMeta = {
      ...meta,
      sourceType,
      uid,
      channel,
      timestamp: now
    };

    ParamBus.set(`${uid}.${channel}`, value, pubMeta);
  }
}


// ===========================
// Velocity
// ===========================

/** No tick for this long → the object has stopped. */
export const VEL_IDLE_MS = 120;

/** Velocity is measured over this much recent movement, not frame to frame. */
export const VEL_WINDOW_MS = 200;

/**
 * Velocity channels for a moving publisher, to spread into its publish() call:
 *
 *   publish("o2p", uid, { t, x, y, angle, ...velocityChannels("o2p", uid, cfg, point.x, point.y, pathLength) });
 *
 * x/y are whatever units the mover actually moves in (score units for o2p,
 * trans and drag; unwrapped degrees for rotate), so `vel` is units per
 * second and `vx`/`vy` its signed components. `span` is how many of those
 * units make one whole of the mover's `t` (the path's length, 360 degrees),
 * and `tvel` is `vel / span`: the rate of `t`, in laps or turns per second.
 * That depends only on the geometry, never on dur — a fader flicked round a
 * 400 m lap reads the same laps/s whatever dur: its cue was written with.
 *
 * Measured over the last VEL_WINDOW_MS of samples rather than between two
 * frames: frame times jitter by a few ms and a single frame's delta turns
 * that straight into velocity noise. `vel` is the distance travelled through
 * the window over its time (arc, so a curve is not read as its chord);
 * vx/vy are the net displacement over the same time.
 *
 * `clock` is the time the position was computed for, in ms. An animation
 * loop should pass its own accumulated tick time: the position is a
 * function of that clock, and reading performance.now() a frame later
 * instead adds the callback's scheduling wobble — a couple of ms on a 200 ms
 * window is a percent of velocity, and that is what shows as jitter in a
 * readout of a perfectly steady mover. Pointer-driven callers just take the
 * default.
 *
 * State lives on `holder` (the cfg, or the element for drag). After
 * VEL_IDLE_MS of real time without a call the same keys are published as 0,
 * so a released fader or a paused animation reads as stopped instead of
 * freezing at its last speed, and the next call starts fresh.
 */
export function velocityChannels(sourceType, uid, holder, x, y, span = 0, clock = performance.now()) {
  const now = clock;
  let s = holder._vel;
  if (!s) {
    // ponytail: fixed window; expose a smooth: param if a score needs a slower or snappier reading
    s = holder._vel = { hist: [], vx: 0, vy: 0, timer: null };
  }
  const prev = s.hist[s.hist.length - 1];
  if (!prev || now > prev.t) {
    s.hist.push({ x, y, t: now, d: prev ? Math.hypot(x - prev.x, y - prev.y) : 0 });
    while (s.hist.length > 2 && now - s.hist[1].t >= VEL_WINDOW_MS) s.hist.shift();
  }

  let vel = 0;
  const first = s.hist[0], latest = s.hist[s.hist.length - 1];
  const dt = (latest.t - first.t) / 1000;
  if (dt > 0) {
    let arc = 0;
    for (let i = 1; i < s.hist.length; i++) arc += s.hist[i].d;
    vel = arc / dt;
    s.vx = (latest.x - first.x) / dt;
    s.vy = (latest.y - first.y) / dt;
  }

  const out = { vel, vx: s.vx, vy: s.vy };
  if (span > 0) out.tvel = vel / span;

  clearTimeout(s.timer);
  s.timer = setTimeout(() => {
    const zeros = {};
    for (const k of Object.keys(out)) zeros[k] = 0;
    s.vx = s.vy = 0; s.hist.length = 0;
    publish(sourceType, uid, zeros);
  }, VEL_IDLE_MS);

  return out;
}


// ===========================
// Window Binding
// ===========================

window.oscillaBinding = {
  bindParam,
  releaseBinding,
  releaseAllBindings,
  ownedBindingCount,
  publish,
  velocityChannels,
  isSignalRef,
  isOscRef,
  mapRange,
  applyCurve
};

// ===========================
// Default Export
// ===========================

export default {
  bindParam,
  publish,
  isSignalRef,
  isOscRef,
  mapRange
};
